// Brand onboarding: 2-step wizard. Step 1 = You (trust). Step 2 = Company (professional).
// Guards: auth + role=brand; if onboardingCompleted redirect to /brand/dashboard.

import React, { useEffect, useState } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import AuthLayout from '../../components/auth/AuthLayout';
import { db } from '../../../firebaseConfig.js';
import { uploadBrandAsset } from '../../lib/uploadBrandAsset.js';
import { useAuth } from '../../context/AuthContext.jsx';
import OnboardingLoading, { ensureMinDuration } from '../../components/onboarding/OnboardingLoading.jsx';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_MB = 2 * 1024 * 1024;

function normalizeWebsite(input) {
  const s = (input || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
}

function isValidUrl(s) {
  try {
    new URL(s);
    return true;
  } catch {
    return false;
  }
}

const currentYear = new Date().getFullYear();

const BrandOnboarding = ({ onNavigate }) => {
  const { user, profile, loading: authLoading } = useAuth();
  const [step, setStep] = useState(1);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [linkedInUrl, setLinkedInUrl] = useState('');
  const [personAvatarFile, setPersonAvatarFile] = useState(null);
  const [personAvatarPreview, setPersonAvatarPreview] = useState('');
  const [personAvatarUploadedUrl, setPersonAvatarUploadedUrl] = useState('');
  const [personAvatarUploading, setPersonAvatarUploading] = useState(false);

  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [startMonth, setStartMonth] = useState('');
  const [startYear, setStartYear] = useState('');
  const [companyLogoFile, setCompanyLogoFile] = useState(null);
  const [companyLogoPreview, setCompanyLogoPreview] = useState('');
  const [companyLogoUploadedUrl, setCompanyLogoUploadedUrl] = useState('');
  const [companyLogoUploading, setCompanyLogoUploading] = useState(false);

  const [step1Touched, setStep1Touched] = useState(false);
  const [step2Touched, setStep2Touched] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user?.email]);

  // All redirects in useEffect to avoid "Cannot update a component while rendering a different component"
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      onNavigate('/login', null, { replace: true });
      return;
    }
    if (profile === null) return; // still loading user doc
    const role = profile?.role ?? profile?.userType;
    if (role !== 'brand') {
      onNavigate('/select-role', null, { replace: true });
      return;
    }
    if (profile?.onboardingCompleted === true || profile?.onboardingStatus === 'completed') {
      onNavigate('/brand/dashboard', null, { replace: true });
    }
  }, [authLoading, user, profile, onNavigate]);

  if (authLoading) {
    return (
      <OnboardingLoading
        label="Loading"
        sublabel="Checking your account."
        progressPercent={50}
      />
    );
  }
  if (!user || profile === null) {
    return null;
  }
  const role = profile?.role ?? profile?.userType;
  if (role !== 'brand') {
    return null;
  }
  if (profile?.onboardingCompleted === true || profile?.onboardingStatus === 'completed') {
    return (
      <OnboardingLoading
        label="Loading"
        sublabel="Taking you to your dashboard."
        progressPercent={50}
      />
    );
  }

  const step1Errors = {};
  if (step1Touched) {
    if (!firstName.trim()) step1Errors.firstName = 'First name is required.';
    if (!lastName.trim()) step1Errors.lastName = 'Last name is required.';
    if (!email.trim()) step1Errors.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) step1Errors.email = 'Enter a valid email.';
    if (linkedInUrl.trim() && !isValidUrl(linkedInUrl.trim())) step1Errors.linkedInUrl = 'Enter a valid URL.';
  }
  const step1Valid =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
    (!linkedInUrl.trim() || isValidUrl(linkedInUrl.trim())) &&
    !!personAvatarUploadedUrl &&
    !personAvatarUploading;

  const step2Errors = {};
  if (step2Touched) {
    if (!companyName.trim()) step2Errors.companyName = 'Company name is required.';
    const w = normalizeWebsite(companyWebsite);
    if (!w) step2Errors.companyWebsite = 'Company website is required.';
    else if (!isValidUrl(w)) step2Errors.companyWebsite = 'Enter a valid website URL.';
    if (!roleTitle.trim()) step2Errors.roleTitle = 'Role title is required.';
    const y = parseInt(startYear, 10);
    if (!startMonth) step2Errors.startMonth = 'Start month is required.';
    if (!startYear) step2Errors.startYear = 'Start year is required.';
    else if (isNaN(y) || y < 1980 || y > currentYear) step2Errors.startYear = `Year must be 1980 to ${currentYear}.`;
  }
  const websiteNorm = normalizeWebsite(companyWebsite);
  const step2Valid =
    companyName.trim() &&
    websiteNorm &&
    isValidUrl(websiteNorm) &&
    roleTitle.trim() &&
    startMonth &&
    startYear &&
    (() => {
      const y = parseInt(startYear, 10);
      return !isNaN(y) && y >= 1980 && y <= currentYear;
    })() &&
    !companyLogoUploading;

  const handlePersonPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Photo must be PNG, JPEG, or WebP.');
      return;
    }
    if (file.size > MAX_MB) {
      setUploadError('Photo must be 2MB or less.');
      return;
    }
    setPersonAvatarFile(file);
    setPersonAvatarPreview(URL.createObjectURL(file));
    setPersonAvatarUploading(true);
    try {
      const url = await uploadBrandAsset(file, user.uid, 'person-avatar');
      setPersonAvatarUploadedUrl(url);
    } catch (err) {
      setUploadError(err.message || 'Upload failed.');
      setPersonAvatarPreview('');
      setPersonAvatarFile(null);
    } finally {
      setPersonAvatarUploading(false);
    }
  };

  const handleCompanyLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadError('Logo must be PNG, JPEG, or WebP.');
      return;
    }
    if (file.size > MAX_MB) {
      setUploadError('Logo must be 2MB or less.');
      return;
    }
    setCompanyLogoFile(file);
    setCompanyLogoPreview(URL.createObjectURL(file));
    setCompanyLogoUploading(true);
    try {
      const url = await uploadBrandAsset(file, user.uid, 'company-logo');
      setCompanyLogoUploadedUrl(url);
    } catch (err) {
      setUploadError(err.message || 'Upload failed.');
      setCompanyLogoPreview('');
      setCompanyLogoFile(null);
    } finally {
      setCompanyLogoUploading(false);
    }
  };

  const onNext = () => {
    setStep1Touched(true);
    if (!step1Valid) return;
    setStep(2);
    setUploadError('');
    setSubmitError('');
  };

  const onFinish = async () => {
    setStep2Touched(true);
    if (!step2Valid || !personAvatarUploadedUrl) return;
    setFinishLoading(true);
    setSubmitError('');
    setUploadError('');
    const uid = user.uid;
    const startMs = Date.now();
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('[BrandOnboarding] Finish started, persisting profile and user doc.');
      }

      let logoUrl = companyLogoUploadedUrl;
      if (companyLogoFile && !companyLogoUploadedUrl) {
        logoUrl = await uploadBrandAsset(companyLogoFile, uid, 'company-logo');
      }

      const brandRef = doc(db, 'brandProfiles', uid);
      await setDoc(
        brandRef,
        {
          contact: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            email: email.trim().toLowerCase(),
            ...(linkedInUrl.trim() ? { linkedInUrl: linkedInUrl.trim() } : {}),
          },
          company: {
            name: companyName.trim(),
            website: websiteNorm,
            roleTitle: roleTitle.trim(),
            timeAtCompany: {
              startMonth: parseInt(startMonth, 10),
              startYear: parseInt(startYear, 10),
            },
            ...(logoUrl ? { logoUrl } : {}),
          },
          media: {
            personAvatarUrl: personAvatarUploadedUrl,
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      await setDoc(
        doc(db, 'users', uid),
        {
          role: 'brand',
          userType: 'brand',
          onboardingCompleted: true,
          onboardingStatus: 'completed',
          updatedAt: serverTimestamp(),
        },
        { merge: true },
      );

      if (process.env.NODE_ENV === 'development') {
        console.log('[BrandOnboarding] Writes done. Waiting for auth hydration before redirect.');
      }
      // Brief delay so AuthContext onSnapshot can deliver updated profile (avoids redirect-to-login race)
      await new Promise((r) => setTimeout(r, 500));
      await ensureMinDuration(startMs, 1200);

      onNavigate('/brand/profile', null, { replace: true });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[BrandOnboarding] Save failed:', err);
      }
      setSubmitError(err.message || 'Something went wrong. Please try again.');
      setFinishLoading(false);
    }
  };

  const personPreviewUrl = personAvatarUploadedUrl || personAvatarPreview;
  const logoPreviewUrl = companyLogoUploadedUrl || companyLogoPreview;

  if (finishLoading) {
    return (
      <OnboardingLoading
        label="Setting up your profile"
        sublabel="Saving your details and taking you to your profile."
        progressPercent={70}
      />
    );
  }

  return (
    <AuthLayout
      title="Set up your brand profile"
      subtitle="This helps creators know who they are working with."
      cardClassName="px-6 py-6 sm:px-8"
    >
      <div className="mb-6">
        <p className="text-sm font-medium text-slate-600">Step {step} of 2</p>
        <div className="mt-2 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
          <div
            className="h-full bg-[#1B9DA2] transition-all duration-300"
            style={{ width: step === 1 ? '50%' : '100%' }}
          />
        </div>
      </div>

      {(uploadError || submitError) && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {uploadError || submitError}
        </div>
      )}

      {step === 1 && (
        <div className="space-y-5">
          <p className="text-sm text-slate-600">Your details so creators know who they are working with.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-slate-700">First name</label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
                placeholder="Jane"
              />
              {step1Errors.firstName && <p className="mt-1 text-xs text-red-500">{step1Errors.firstName}</p>}
            </div>
            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-slate-700">Last name</label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
                placeholder="Doe"
              />
              {step1Errors.lastName && <p className="mt-1 text-xs text-red-500">{step1Errors.lastName}</p>}
            </div>
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700">Work email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
              placeholder="you@company.com"
            />
            <p className="mt-1 text-xs text-slate-500">Work email recommended</p>
            {step1Errors.email && <p className="mt-1 text-xs text-red-500">{step1Errors.email}</p>}
          </div>
          <div>
            <label htmlFor="linkedInUrl" className="block text-sm font-medium text-slate-700">LinkedIn profile (optional)</label>
            <input
              id="linkedInUrl"
              type="url"
              value={linkedInUrl}
              onChange={(e) => setLinkedInUrl(e.target.value)}
              placeholder="https://linkedin.com/in/..."
              className="mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            />
            {step1Errors.linkedInUrl && <p className="mt-1 text-xs text-red-500">{step1Errors.linkedInUrl}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Your photo (required)</label>
            <div className="mt-2 flex items-center gap-4">
              <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-full border-2 border-slate-200 bg-slate-100">
                {personPreviewUrl ? (
                  <img src={personPreviewUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-slate-400 text-xs">Photo</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handlePersonPhoto}
                  disabled={personAvatarUploading}
                  className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200 disabled:opacity-50"
                />
                {personAvatarUploading && <p className="mt-1 text-xs text-slate-500">Uploading...</p>}
                {!personAvatarUploadedUrl && !personAvatarUploading && (
                  <p className="mt-1 text-xs text-slate-500">PNG, JPEG, or WebP. Max 2MB.</p>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onNext}
            disabled={!step1Valid}
            className="h-12 w-full rounded-lg bg-[#1B9DA2] text-sm font-medium text-white transition hover:bg-[#168589] disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <p className="text-sm text-slate-600">Company details so we can show your professional context.</p>
          <div>
            <label htmlFor="companyName" className="block text-sm font-medium text-slate-700">Company name</label>
            <input
              id="companyName"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
              placeholder="Acme Inc"
            />
            {step2Errors.companyName && <p className="mt-1 text-xs text-red-500">{step2Errors.companyName}</p>}
          </div>
          <div>
            <label htmlFor="companyWebsite" className="block text-sm font-medium text-slate-700">Company website</label>
            <input
              id="companyWebsite"
              type="text"
              value={companyWebsite}
              onChange={(e) => setCompanyWebsite(e.target.value)}
              placeholder="example.com or https://example.com"
              className="mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            />
            {step2Errors.companyWebsite && <p className="mt-1 text-xs text-red-500">{step2Errors.companyWebsite}</p>}
          </div>
          <div>
            <label htmlFor="roleTitle" className="block text-sm font-medium text-slate-700">Role title</label>
            <input
              id="roleTitle"
              type="text"
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              placeholder="e.g. CEO, Head of Growth, Influencer Marketing Manager"
              className="mt-1 h-11 w-full rounded-lg border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            />
            {step2Errors.roleTitle && <p className="mt-1 text-xs text-red-500">{step2Errors.roleTitle}</p>}
          </div>
          <div>
            <span className="block text-sm font-medium text-slate-700">Time at company</span>
            <div className="mt-2 flex flex-wrap gap-3">
              <select
                value={startMonth}
                onChange={(e) => setStartMonth(e.target.value)}
                className="h-11 rounded-lg border border-slate-300 px-3 text-sm text-slate-900 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2] min-w-[120px]"
              >
                <option value="">Month</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <input
                type="number"
                min={1980}
                max={currentYear}
                value={startYear}
                onChange={(e) => setStartYear(e.target.value)}
                placeholder="Year"
                className="h-11 w-24 rounded-lg border border-slate-300 px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#1B9DA2] focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
              />
            </div>
            {(step2Errors.startMonth || step2Errors.startYear) && (
              <p className="mt-1 text-xs text-red-500">{step2Errors.startMonth || step2Errors.startYear}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700">Company logo (optional)</label>
            <div className="mt-2 flex items-center gap-4">
              <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg border-2 border-slate-200 bg-slate-100">
                {logoPreviewUrl ? (
                  <img src={logoPreviewUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-slate-400 text-xs">Logo</div>
                )}
              </div>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleCompanyLogo}
                disabled={companyLogoUploading}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-100 file:px-4 file:py-2 file:text-sm file:font-medium file:text-slate-700 hover:file:bg-slate-200 disabled:opacity-50"
              />
              {companyLogoUploading && <p className="mt-1 text-xs text-slate-500">Uploading...</p>}
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="h-12 flex-1 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-[#1B9DA2]"
            >
              Back
            </button>
            <button
              type="button"
              onClick={onFinish}
              disabled={!step2Valid || finishLoading}
              className="h-12 flex-1 rounded-lg bg-[#1B9DA2] text-sm font-medium text-white transition hover:bg-[#168589] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {finishLoading ? 'Saving...' : 'Finish'}
            </button>
          </div>
        </div>
      )}
    </AuthLayout>
  );
};

export default BrandOnboarding;
